import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import type { TemplatesType, WidgetProps } from "@rjsf/utils";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { plex } from "../utils/fonts";

const optionGroupHeadingSx = {
  fontFamily: plex,
  fontWeight: 500,
  fontSize: "15px",
  lineHeight: 1.3,
} as const;

// ---------------- FieldTemplate ----------------

const FieldTemplate: TemplatesType["FieldTemplate"] = (props) => {
  const { description, errors, help, children, schema, uiSchema } = props;

  const widget = uiSchema?.["ui:widget"];
  const isBoolean = schema.type === "boolean";
  const isRadio = widget === "radio";
  const isCheckboxes = widget === "checkboxes";
  const isOptionGroup = isBoolean || isRadio || isCheckboxes;

  const renderDescription = () => {
    if (!description) return null;

    return (
      <Box
        mt={0.25}
        sx={{
          "& p, & span": {
            fontSize: "0.75rem",
            margin: 0,
            color: "#555",
          },
        }}
      >
        {description}
      </Box>
    );
  };

  return (
    <Box mb={2}>
      {isOptionGroup ? (
        // radios / checkboxes
        <>{children}</>
      ) : (
        <>
          {children}
          {renderDescription()}
        </>
      )}

      {errors && (
        <FormHelperText
          error
          sx={{
            mt: 0.25,
            ml: 0,
            pl: 0,
            fontSize: "0.75rem",
            m: 0,
            // force standard MUI error color
            color: (theme) => theme.palette.error.main,
            "& *": {
              color: (theme) => `${theme.palette.error.main} !important`,
            },
          }}
        >
          {errors}
        </FormHelperText>
      )}

      {help}
    </Box>
  );
};
// ---------------- RadioWidget (string enum, single choice) ----------------

export const RadioWidget = (props: WidgetProps) => {
  const {
    id,
    label,
    required,
    options,
    value,
    disabled,
    readonly,
    onChange,
    schema,
    rawErrors,
  } = props;
  const { enumOptions, inline } = options;
  const hasError = !!rawErrors?.length;

  return (
    <FormControl
      component="fieldset"
      disabled={disabled || readonly}
      error={hasError}
      sx={{
        mb: 1,
        ...(hasError
          ? {
              "& .MuiFormLabel-root": {
                color: (theme) => theme.palette.error.main,
              },
              "& .MuiFormControlLabel-label": {
                color: (theme) => theme.palette.error.main,
              },
              "& .MuiRadio-root": {
                color: (theme) => theme.palette.error.main,
              },
              "& .MuiRadio-root.Mui-checked": {
                color: "var(--primary-color)",
              },
            }
          : {}),
      }}
    >
      <FormLabel component="legend" focused={false} sx={optionGroupHeadingSx}>
        {label}
        {required ? " *" : ""}
      </FormLabel>

      {schema.description && (
        <FormHelperText
          error={false}
          sx={{
            mt: 0.25,
            ml: 0,
            pl: 0,
            fontWeight: 300,
            color: "inherit",
          }}
        >
          {schema.description}
        </FormHelperText>
      )}

      <RadioGroup
        id={id}
        row={!!inline}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
      >
        {enumOptions?.map((opt) => (
          <FormControlLabel
            key={String(opt.value)}
            value={opt.value}
            control={<Radio />}
            label={opt.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

// ---------------- CheckboxesWidget (array-of-enum) ----------------

export const CheckboxesWidget = (props: WidgetProps) => {
  const {
    id,
    label,
    required,
    options,
    value,
    disabled,
    readonly,
    onChange,
    schema,
    rawErrors,
  } = props;

  const anyOptions = options as any;
  const hasError = !!rawErrors?.length;

  const enumOptions =
    anyOptions.enumOptions ??
    ((schema as any)?.items?.enum || []).map((val: any) => ({
      label: String(val),
      value: val,
    }));

  const current: any[] = Array.isArray(value) ? value : [];

  const handleToggle =
    (optionValue: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      let next = [...current];

      if (checked) {
        if (!next.includes(optionValue)) {
          next.push(optionValue);
        }
      } else {
        next = next.filter((v) => v !== optionValue);
      }

      onChange(next);
    };

  return (
    <FormControl
      component="fieldset"
      disabled={disabled || readonly}
      error={hasError}
      // sx={{ mb: 1 }}
      sx={{
        mb: 1,
        ...(hasError
          ? {
              "& .MuiFormLabel-root": {
                color: (theme) => theme.palette.error.main,
              },
              "& .MuiFormControlLabel-label": {
                color: (theme) => theme.palette.error.main,
              },
              "& .MuiCheckbox-root": {
                color: (theme) => theme.palette.error.main,
              },
              "& .MuiCheckbox-root.Mui-checked": {
                color: "red",
              },
            }
          : {}),
      }}
    >
      <FormLabel component="legend" focused={false} sx={optionGroupHeadingSx}>
        {label}
        {required ? " *" : ""}
      </FormLabel>

      {schema.description && (
        <FormHelperText
          sx={{
            mt: 0.25,
            ml: 0,
            pl: 0,
            fontWeight: 300,
          }}
        >
          {schema.description}
        </FormHelperText>
      )}

      <Box
        id={id}
        sx={{
          display: "flex",
          flexDirection: "column",
          mt: 0.5,
        }}
      >
        {enumOptions.map((opt: any) => {
          const optValue = opt.value;
          const checked = current.includes(optValue);

          return (
            <FormControlLabel
              key={String(optValue)}
              control={
                <Checkbox checked={checked} onChange={handleToggle(optValue)} />
              }
              label={opt.label}
              // small vertical spacing
              sx={{ mb: 0.25 }}
            />
          );
        })}
      </Box>
    </FormControl>
  );
};
// ---------------- Single Checkbox for booleans ----------------

export const CheckboxWidget = (props: WidgetProps) => {
  const {
    id,
    label,
    required,
    value,
    disabled,
    readonly,
    onChange,
    schema,
    rawErrors,
  } = props;
  const hasError = !!rawErrors?.length;

  return (
    <FormControl
      component="fieldset"
      disabled={disabled || readonly}
      error={hasError}
      sx={{ mb: 1 }}
    >
      <FormControlLabel
        control={
          <Checkbox
            id={id}
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        sx={
          hasError
            ? {
                "& .MuiFormControlLabel-label": {
                  color: (theme) => theme.palette.error.main,
                },
                "& .MuiCheckbox-root": {
                  color: (theme) => theme.palette.error.main,
                },
                "& .MuiCheckbox-root.Mui-checked": {
                  color: "var(--primary-color)",
                },
              }
            : undefined
        }
        label={
          <>
            {label}
            {required ? " *" : ""}
          </>
        }
      />
      {schema.description && (
        <FormHelperText sx={{ mt: 0.25, ml: 0, pl: 0, fontWeight: 500 }}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// ---------------- Title / Description / Object templates ----------------

const TitleFieldTemplate: TemplatesType["TitleFieldTemplate"] = ({
  id,
  title,
}) => {
  if (!title) return null;
  return (
    <Box mb={1.5}>
      <Typography
        id={id}
        variant="h6"
        component="p"
        sx={{ fontFamily: plex, fontWeight: 500, lineHeight: 1.25 }}
      >
        {title}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
};

const DescriptionFieldTemplate: TemplatesType["DescriptionFieldTemplate"] = ({
  description,
}) => {
  if (!description) return null;

  return (
    <Typography
      variant="body2"
      component="p"
      sx={{
        fontFamily: plex,
        fontWeight: 400,
        fontSize: "0.875rem",
        color: "#000",
        mb: 0.5,
      }}
    >
      {description}
    </Typography>
  );
};

const ObjectFieldTemplate: TemplatesType["ObjectFieldTemplate"] = ({
  title,
  description,
  properties,
  required,
  idSchema,
}) => {
  return (
    <Box mb={2.5}>
      {title ? (
        <Typography
          id={`${idSchema?.$id}__title`}
          variant="h6"
          component="p"
          sx={{
            fontFamily: plex,
            fontWeight: 500,
            mb: description ? 0.5 : 1.5,
          }}
        >
          {title}
          {required ? " *" : ""}
        </Typography>
      ) : null}

      {description ? (
        <Typography
          id={`${idSchema?.$id}__description`}
          variant="body2"
          component="p"
          sx={{
            fontFamily: plex,
            fontWeight: 500,
            fontSize: 15,
            color: "text.secondary",
            mb: 1,
          }}
        >
          {description}
        </Typography>
      ) : null}

      <Box mt={title || description ? 1 : 0}>
        {properties.map((p) => p.content)}
      </Box>
    </Box>
  );
};

const NumberField = (props: WidgetProps) => {
  const {
    id,
    label,
    required,
    value,
    disabled,
    readonly,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    rawErrors,
  } = props;

  return (
    <TextField
      id={id}
      label={label}
      required={required}
      type="text"
      value={value ?? ""}
      placeholder={placeholder}
      disabled={disabled || readonly}
      error={!!rawErrors?.length}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw.trim() === "") {
          onChange(undefined);
          return;
        }

        // dont allow decimal in input
        const normalized = raw.replace(/\./g, "");
        const n = Number(normalized);
        onChange(Number.isNaN(n) ? value : n);
      }}
      onBlur={(e) => onBlur?.(id, e.target.value)}
      onFocus={(e) => onFocus?.(id, e.target.value)}
    />
  );
};

export {
  TitleFieldTemplate,
  DescriptionFieldTemplate,
  ObjectFieldTemplate,
  FieldTemplate,
  NumberField,
};
